import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit
} from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import moment from 'moment';
import { ActionRecordsHttpService } from '../../services/action-records-http.service';
import { SettingsActionRecordDisplay, SettingsActionRecordExtras } from './settings-action-record.model';
import {
  ActionType, ActionStatus, ActionDomain, ACTION_TYPE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS
} from '../../constants/action-record.constant';
import { BringozDateRange } from '../../../../commons/angular/models/bringoz-date-range.model';
import { AuditLogEntry, AuditLogStatus } from '../../../../commons/angular/services/audit-log-parser.service';
import { PanelService } from '../../../../commons/angular/services/panel.service';
import { SettingsAuditLogDetailComponent } from './settings-audit-log-detail.component';

require('./settings-audit-log.component.scss');

@Component({
  standalone: false,
  selector: 'app-settings-audit-log-view',
  templateUrl: './settings-audit-log.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsAuditLogComponent implements OnInit, OnDestroy {

  public readonly AuditLogStatus = AuditLogStatus;

  public readonly tableUniqueId: string = 'settings-audit-log';
  public readonly rowHeight: number = 48;
  public readonly skeletonRows: undefined[] = Array.from({ length: 10 });
  public readonly defaultWidths: { [key: string]: number } = {
    'col-details': 40,
    'col-timestamp': 170,
    'col-actor': 200,
    'col-action': 180,
    'col-domain': 150,
    'col-status': 140,
    'col-description': 675
  };

  public records: SettingsActionRecordDisplay[] = [];

  public isLoading = false;
  public isInitialized = false;
  public nextCursor?: string;
  public size = 30;

  public selectedActionFilter = 'ALL';
  public selectedStatusFilter = 'ALL';
  public selectedActions: ActionType[] = [];
  public selectedStatus?: ActionStatus;
  public fromDate: number;
  public toDate: number;
  public selectedRange: BringozDateRange;
  public customTemplates: BringozDateRange[];

  public readonly actionFilterOptions = ACTION_TYPE_FILTER_OPTIONS;
  public readonly statusFilterOptions = STATUS_FILTER_OPTIONS;

  private sub: Subscription;
  private allRecords: any[] = [];

  constructor(
    @Inject(ActionRecordsHttpService) private actionRecordsHttpService: ActionRecordsHttpService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PanelService) private panelService: PanelService,
    @Inject('dateRangeTimeFiltersService') private dateRangeTimeFiltersService: any
  ) {
  }

  ngOnInit(): void {
    this.customTemplates = this.dateRangeTimeFiltersService.getDeliveriesCustomTemplates();
    this.selectedRange = this.dateRangeTimeFiltersService.thisMonth();
    this.fromDate = this.selectedRange.dateStart;
    this.toDate = this.selectedRange.dateEnd;
    this.fetchLogs();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  public fetchLogs(isLoadMore: boolean = false): void {
    if (this.isLoading || (!isLoadMore && this.records.length > 0)) {
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    const params: any = {
      limit: this.size,
      domains: `${ActionDomain.GLOBAL_SETTINGS},${ActionDomain.SETTINGS}`
    };

    if (isLoadMore && this.nextCursor) {
      params.cursor = this.nextCursor;
    }

    if (this.selectedActions.length > 0) {
      params.actions = this.selectedActions.join(',');
    }

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.fromDate) {
      params.from = this.fromDate;
    }

    if (this.toDate) {
      params.to = this.toDate;
    }

    this.sub?.unsubscribe();
    this.sub = this.actionRecordsHttpService.getActionRecords(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isInitialized = true;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          const newList = res.list || [];
          const parsedRecords = this.formatRecords(newList);
          if (isLoadMore) {
            this.allRecords = [...this.allRecords, ...newList];
            this.records = [...this.records, ...parsedRecords];
          } else {
            this.allRecords = newList;
            this.records = parsedRecords;
          }
          this.nextCursor = res.cursor;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching action records:', err);
          if (!isLoadMore) {
            this.records = [];
          }
        }
      });
  }

  onFetchNext(): void {
    if (!this.nextCursor || this.isLoading) {
      return;
    }
    this.fetchLogs(true);
  }

  resetAndFetch(): void {
    this.sub?.unsubscribe();
    this.isLoading = false;
    this.records = [];
    this.allRecords = [];
    this.nextCursor = undefined;
    this.fetchLogs();
  }

  onActionFilterChange(value: string): void {
    this.selectedActionFilter = value;
    this.selectedActions = value === 'ALL' ? [] : [value as ActionType];
    this.resetAndFetch();
  }

  onStatusFilterChange(value: string): void {
    this.selectedStatusFilter = value;
    this.selectedStatus = value === 'ALL' ? undefined : (value as ActionStatus);
    this.resetAndFetch();
  }

  onRangeChange(range: BringozDateRange): void {
    if (range) {
      this.selectedRange = range;
      this.fromDate = range.dateStart;
      this.toDate = range.dateEnd;
      this.resetAndFetch();
    }
  }

  openDetail(record: SettingsActionRecordDisplay): void {
    const centeredEvent = { target: document.body };
    this.panelService.open(SettingsAuditLogDetailComponent, centeredEvent, { record }, { hasBackdrop: true, width: '640px', height: 'auto' });
  }

  private formatDate(timestamp: number): string {
    if (!timestamp) return '';
    return moment(timestamp).format('MM/DD/YY HH:mm');
  }

  private formatRecords(records: (AuditLogEntry & SettingsActionRecordExtras)[]): SettingsActionRecordDisplay[] {
    if (!records) return [];
    return records.map(record => ({
      id: record.id,
      performedAt: record.performedAt,
      formattedDate: this.formatDate(record.performedAt),
      actor: record.actor?.name || record.actor?.source || '',
      action: record.action || record.operation || 'Unknown',
      domain: record.domain || record.domains?.[0] || 'System',
      status: record.status,
      rawRecord: record
    }));
  }
}
